// useBases — PowerSync-backed hook for the capture home page's base list.
// Mirrors the pattern of `useSites` (Phase D-2) but at the higher level
// of the capture drill-down: bases scoped via the user's
// `assigned_sites` claim through the `reference_site_scoped` stream.
//
// Site/building counts are derived via correlated subqueries against
// the local SQLite mirror so the totals reflect what the user can
// actually navigate into. This matches the contractually significant
// invariant from AGENTS.md core principle: counts must accurately
// represent the data the user sees offline.
//
// Sort order follows buildspec §5.4a: display_order ASC, name ASC.
//
// Sync-readiness gate: the empty state is gated on the
// `reference_site_scoped` stream (the same stream `useSites` watches).
// Without the gate, a freshly-installed device would briefly flash
// "No bases configured" before the stream's first sync delivered any
// rows. Cached non-empty data is surfaced immediately, matching the
// offline-first contract.
//
// Stale-data guard: not needed here because `useBases` takes no scope
// parameter — every call queries the same global "bases I can see"
// set. (Compare with `useSites(baseId)`, where the parameter changes
// drive a guard against showing the prior base's sites under a new
// URL.)
//
// See docs/plans/powersync-implementation.md Phase D for context.
//
// Imports: kept on single lines because lint:architecture's awk scan
// only treats lines starting with `import `/`from ` as imports —
// continuation lines from a multi-line import look like raw matches
// and would trip Rule 4. Mirrors the pattern in `connector.ts` and
// `provider.tsx`.
import { useAllSyncStreamsHaveSynced, useQuery } from '@powersync/react'

import { db } from './client'
import type { BaseRecord } from './schema'

/**
 * Row type returned by `useBases`. Snake_case mirrors the local SQLite
 * column names; consumers map to camelCase as needed. The two count
 * fields are derived columns (correlated subqueries) — they are not
 * persisted on the `bases` table itself.
 */
export interface UseBasesRow extends BaseRecord {
  /** id is auto-created by PowerSync — always present at runtime */
  id: string
  /** Sites under this base in the local SQLite mirror */
  site_count: number
  /** Buildings under this base in the local SQLite mirror */
  building_count: number
}

export interface UseBasesResult {
  bases: UseBasesRow[]
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

/**
 * Returns the bases list visible to the signed-in user. Scoping is
 * applied at the sync-rule level (auth.parameter('assigned_sites')) so
 * the local SQLite mirror only contains rows the user has access to.
 * The hook applies no further authorization — it just projects the
 * mirror.
 *
 * Reactive: any sync-delivered change to the `bases`, `sites`, or
 * `buildings` table triggers an automatic re-render.
 */
export function useBases(): UseBasesResult {
  const basesResult = useQuery<UseBasesRow>(
    `SELECT
       b.id,
       b.contract_id,
       b.name,
       b.code,
       b.display_order,
       b.created_at,
       b.updated_at,
       (SELECT COUNT(*) FROM sites s WHERE s.base_id = b.id) AS site_count,
       (SELECT COUNT(*) FROM buildings bg
          INNER JOIN sites s ON bg.site_id = s.id
          WHERE s.base_id = b.id
            -- Consistent with the field building list: admin-only
            -- statuses (to_be_demolished / out_of_scope) are hidden
            -- from field staff (see use-buildings.ts).
            AND bg.status NOT IN ('to_be_demolished', 'out_of_scope')) AS building_count
     FROM bases b
     ORDER BY b.display_order ASC, b.name ASC`,
    [],
  )

  // Same scoped sync-readiness check as `useSites` — the empty state
  // is only authoritative once the `reference_site_scoped` stream has
  // delivered its initial sync.
  const referenceSiteScopedSynced = useAllSyncStreamsHaveSynced(db, [
    { name: 'reference_site_scoped', waitForStream: true },
  ])

  const basesAreEmptyButUnsynced =
    basesResult.data.length === 0 && !referenceSiteScopedSynced

  const isLoading = basesResult.isLoading || basesAreEmptyButUnsynced

  return {
    bases: basesResult.data,
    isLoading,
    isFetching: basesResult.isFetching,
    error: basesResult.error,
  }
}
